import { State } from './StateMachine.js';

// Idle State - mob is waiting/looking around
export class IdleState extends State {
    constructor() {
        super();
        this.idleTimer = 0;
        this.idleWaitTime = 500; // 0.5 seconds in idle before checking for player
    }

    enter() {
        this.idleTimer = 0;
        //console.log(`IdleState.enter() called for ${this.owner.mobType} at (${Math.round(this.owner.x)}, ${Math.round(this.owner.y)})`);
        this.owner.playAnimation('idle');
        this.owner.setVelocity(0, 0);
    }

    update(deltaTime) {
        this.idleTimer += deltaTime;

        // Debug for jump zombies - log every update to see what's happening
        if (this.owner.mobType === 'jump_zombie') {
            const canSeePlayer = this.owner.canSeePlayer();
            const inAttackRange = this.owner.isPlayerInAttackRange();
            const playerDistance = this.owner.getDistanceToPlayer();
            
            //console.log(`[${this.owner.scene.time.now}] Jump zombie idle update: canSee=${canSeePlayer}, attackRange=${inAttackRange}, distance=${Math.round(playerDistance)}, timer=${Math.round(this.idleTimer)}`);
        }

        // Check for player in range
        if (this.owner.canSeePlayer()) {
            if (this.owner.isPlayerInAttackRange()) {
                this.stateMachine.changeState('AttackState');
            } else {
                this.stateMachine.changeState('WalkState');
            }
            return;
        }

        // Stay idle for a bit, then maybe patrol or continue idle
        if (this.idleTimer >= this.idleWaitTime) {
            if (this.owner.shouldPatrol && Math.random() < 0.3) {
                this.stateMachine.changeState('PatrolState');
            } else {
                this.idleTimer = 0; // Reset timer to stay idle longer
            }
        }
    }
}

// Walk State - mob is moving towards player or patrolling
export class WalkState extends State {
    constructor() {
        super();
        this.stuckTimer = 0;
        this.maxStuckTime = 2000; // 2 seconds
        this.lastPosition = { x: 0, y: 0 };
    }

    enter() {
        //console.log(`WalkState.enter() called for ${this.owner.mobType} at (${Math.round(this.owner.x)}, ${Math.round(this.owner.y)})`);
        this.owner.playAnimation('walk');
        this.stuckTimer = 0;
        this.lastPosition.x = this.owner.x;
        this.lastPosition.y = this.owner.y;
    }

    update(deltaTime) {
        // Check if player is still visible
        if (!this.owner.canSeePlayer()) {
            this.stateMachine.changeState('IdleState');
            return;
        }

        // Check if player is in attack range
        if (this.owner.isPlayerInAttackRange()) {
            this.stateMachine.changeState('AttackState');
            return;
        }

        // Move towards player
        this.owner.moveTowardsPlayer();

        // Check if mob is stuck
        const distanceMoved = Phaser.Math.Distance.Between(
            this.lastPosition.x, this.lastPosition.y,
            this.owner.x, this.owner.y
        );

        if (distanceMoved < 1) { // If barely moved
            this.stuckTimer += deltaTime;
            if (this.stuckTimer >= this.maxStuckTime) {
                this.stateMachine.changeState('IdleState');
                return;
            }
        } else {
            this.stuckTimer = 0;
            this.lastPosition.x = this.owner.x;
            this.lastPosition.y = this.owner.y;
        }
    }
}

// Patrol State - mob moves in a pattern when no player is detected
export class PatrolState extends State {
    constructor() {
        super();
        this.patrolDirection = 1; // 1 for right, -1 for left
        this.patrolDistance = 100;
        this.startX = 0;
        this.patrolTimer = 0;
        this.patrolWaitTime = 2000; // Wait 2 seconds at each end
    }

    enter() {
        this.owner.playAnimation('walk');
        this.startX = this.owner.x;
        this.patrolTimer = 0;
    }

    update(deltaTime) {
        // Check for player
        if (this.owner.canSeePlayer()) {
            this.stateMachine.changeState('WalkState');
            return;
        }

        // Patrol logic
        const distanceFromStart = this.owner.x - this.startX;
        
        if (Math.abs(distanceFromStart) >= this.patrolDistance) {
            this.patrolDirection *= -1;
            this.patrolTimer = 0;
        }

        if (this.patrolTimer < this.patrolWaitTime) {
            this.patrolTimer += deltaTime;
            this.owner.setVelocity(0, 0);
        } else {
            this.owner.setVelocity(this.patrolDirection * this.owner.speed * 0.5, 0);
        }
    }
}

// Attack State - mob is attacking the player
export class AttackState extends State {
    constructor() {
        super();
        this.attackCooldown = 0;
        this.hasAttacked = false;
        this.soundPlayed = false; // Add flag to prevent multiple sound plays
    }

    enter() {
        this.owner.playAnimation('attack');
        this.owner.setVelocity(0, 0);
        this.attackCooldown = this.owner.attackCooldownTime;
        this.hasAttacked = false;
        this.soundPlayed = false;

        // Play attack sound early in the animation (around frame 3-4)
        // With 8 FPS and wanting sound at frame 3: 3/8 = 0.375 seconds = 375ms
        this.owner.scene.time.delayedCall(375, () => {
            if (!this.soundPlayed) {
                this.owner.playAttackSound();
                this.soundPlayed = true;
            }
        });

        // Listen for animation complete to know when attack lands
        this.owner.sprite.once('animationcomplete', this.onAttackComplete, this);
    }

    update(deltaTime) {
        this.attackCooldown -= deltaTime;

        // Check if player moved out of range during attack
        if (!this.owner.isPlayerInAttackRange() && this.attackCooldown <= 0) {
            this.stateMachine.changeState('WalkState');
            return;
        }

        // Check if player is no longer visible
        if (!this.owner.canSeePlayer() && this.attackCooldown <= 0) {
            this.stateMachine.changeState('IdleState');
            return;
        }

        // Attack cooldown finished, ready for next attack
        if (this.attackCooldown <= 0 && this.hasAttacked) {
            if (this.owner.isPlayerInAttackRange()) {
                this.enter(); // Start new attack
            } else {
                this.stateMachine.changeState('WalkState');
            }
        }
    }

    onAttackComplete() {
        this.hasAttacked = true;
        
        // Deal damage to player if still in range (sound already played during animation)
        if (this.owner.isPlayerInAttackRange()) {
            this.owner.dealDamageToPlayer();
        }
    }

    exit() {
        this.owner.sprite.off('animationcomplete', this.onAttackComplete, this);
    }
}

// Hurt State - mob is taking damage
export class HurtState extends State {
    constructor() {
        super();
        this.hurtDuration = 500; // 0.5 seconds
        this.hurtTimer = 0;
    }

    enter() {
        this.owner.playAnimation('hurt');
        this.owner.setVelocity(0, 0);
        this.hurtTimer = 0;
        
        // Knockback effect
        if (this.owner.lastDamageDirection) {
            const knockbackForce = 50;
            this.owner.setVelocity(
                this.owner.lastDamageDirection.x * knockbackForce,
                this.owner.lastDamageDirection.y * knockbackForce
            );
        }
    }

    update(deltaTime) {
        this.hurtTimer += deltaTime;

        if (this.hurtTimer >= this.hurtDuration) {
            // Return to appropriate state based on situation
            if (this.owner.canSeePlayer()) {
                if (this.owner.isPlayerInAttackRange()) {
                    this.stateMachine.changeState('AttackState');
                } else {
                    this.stateMachine.changeState('WalkState');
                }
            } else {
                this.stateMachine.changeState('IdleState');
            }
        }
    }
}

// Death State - mob is dying/dead
export class DeathState extends State {
    constructor() {
        super();
        this.deathAnimationComplete = false;
    }

    enter() {
        this.owner.playAnimation('death');
        this.owner.setVelocity(0, 0);
        this.owner.isDead = true;
        
        // Listen for death animation to complete
        this.owner.sprite.once('animationcomplete', this.onDeathComplete, this);
    }

    update(deltaTime) {
        // Death state doesn't transition to other states
        // The mob should be removed from the scene after death animation
    }

    onDeathComplete() {
        this.deathAnimationComplete = true;
        this.owner.onDeathAnimationComplete();
    }

    exit() {
        this.owner.sprite.off('animationcomplete', this.onDeathComplete, this);
    }
}

// Skill State - for boss enemies with special abilities (like skeleton knight shield bash)
export class SkillState extends State {
    constructor() {
        super();
        this.skillCooldown = 0;
        this.hasExecutedSkill = false;
        this.soundPlayed = false; // Add flag to prevent multiple sound plays
    }

    enter() {
        this.owner.playAnimation('skill');
        this.owner.setVelocity(0, 0);
        this.skillCooldown = this.owner.skillCooldownTime || 3000;
        this.hasExecutedSkill = false;
        this.soundPlayed = false;

        // Play skill sound early in the animation (around frame 2-3)
        // With 8 FPS and wanting sound at frame 2: 2/8 = 0.25 seconds = 250ms
        this.owner.scene.time.delayedCall(250, () => {
            if (!this.soundPlayed) {
                this.owner.playSkillSound();
                this.soundPlayed = true;
            }
        });

        // Listen for skill animation to complete
        this.owner.sprite.once('animationcomplete', this.onSkillComplete, this);
    }

    update(deltaTime) {
        this.skillCooldown -= deltaTime;

        // Skill animation finished
        if (this.hasExecutedSkill && this.skillCooldown <= 0) {
            // Return to combat based on player position
            if (this.owner.canSeePlayer()) {
                if (this.owner.isPlayerInAttackRange()) {
                    this.stateMachine.changeState('AttackState');
                } else {
                    this.stateMachine.changeState('WalkState');
                }
            } else {
                this.stateMachine.changeState('IdleState');
            }
        }
    }

    onSkillComplete() {
        this.hasExecutedSkill = true;
        // Execute skill effect (shield bash, area damage, etc.) - but don't play sound here anymore
        this.owner.executeSkillEffect();
    }

    exit() {
        this.owner.sprite.off('animationcomplete', this.onSkillComplete, this);
    }
}

// Jump Attack State - for jump zombies
export class JumpState extends State {
    constructor() {
        super();
        this.jumpDuration = 800; // Fast lunge attack
        this.jumpTimer = 0;
        this.hasAttacked = false;
        this.damageDealt = false;
        this.landingThreshold = 0.4; // When to switch to fall animation
    }

    enter() {        
        // Play attack animation (shows lunging)
        this.owner.playAnimation('attack');
        this.jumpTimer = 0;
        this.hasAttacked = false;
        this.damageDealt = false;
        
        // Calculate lunge trajectory towards player
        const playerDirection = this.owner.getDirectionToPlayer();
        const lungeForce = 300; // Fast lunge speed
        
        // Face the player
        this.owner.facingDirection = playerDirection.x > 0 ? 1 : -1;
        
        // Launch towards player
        this.owner.setVelocity(
            playerDirection.x * lungeForce,
            playerDirection.y * lungeForce
        );
        
    }

    update(deltaTime) {
        this.jumpTimer += deltaTime;

        // Check for damage dealing during the middle of the lunge
        if (!this.damageDealt && this.jumpTimer >= this.jumpDuration * 0.3 && this.jumpTimer <= this.jumpDuration * 0.7) {
            const distanceToPlayer = this.owner.getDistanceToPlayer();
            
            // Deal damage if close enough to player during lunge
            if (distanceToPlayer <= this.owner.attackRange * 1.5) { // Slightly larger hitbox for lunge
                this.dealLungeDamage();
            }
        }

        // Transition to fall animation partway through
        if (!this.hasAttacked && this.jumpTimer >= this.jumpDuration * this.landingThreshold) {
            this.hasAttacked = true;
            this.owner.playAnimation('fall');
            
            // Slow down after the main lunge
            const currentVel = this.owner.sprite.body.velocity;
            this.owner.setVelocity(currentVel.x * 0.3, currentVel.y * 0.3);
        }

        // Jump attack finished
        if (this.jumpTimer >= this.jumpDuration) {
            this.owner.setVelocity(0, 0); // Stop movement
            
            // Transition to appropriate state
            if (this.owner.canSeePlayer()) {
                this.stateMachine.changeState('WalkState');
            } else {
                this.stateMachine.changeState('IdleState');
            }
        }
    }

    dealLungeDamage() {
        if (this.damageDealt) return; // Only deal damage once per lunge
        
        this.damageDealt = true;
        const player = this.owner.getPlayer();
        
        if (player) {            
            // Play attack sound for lunge attack
            this.owner.playAttackSound();
            
            player.modifyHealth(-this.owner.damage);
            
            // Visual feedback - brief red tint on player
            if (player.sprite) {
                player.sprite.setTint(0xff4444);
                this.owner.scene.time.delayedCall(200, () => {
                    if (player.sprite) {
                        player.sprite.clearTint();
                    }
                });
            }
        }
    }

    exit() {
        this.owner.setVelocity(0, 0); // Ensure we stop moving
    }
} 